/**
 * @file lib/audit-log-i18n.ts
 * @description 操作日誌（audit log）顯示用的中文翻譯層。
 *
 * 背景：後端 `auditLogMiddleware` 自動記錄的 `action` 是「HTTP 方法 + 路由模式」
 * （例：`PATCH /api/cases/:id/payments/:id/confirm`），`entityType` 取路徑第二段
 * （例：`cases`），`entityId` 取路徑首個數字 ID。這些對非技術使用者不可讀，
 * 本模組把它們翻成中文「操作對象 · 動作」短語，純前端展示，不改動後端落庫格式。
 *
 * 另有少量 service 層顯式寫入的點分動作（`client.update` / `recycle-bin.restore`
 * / `recycle-bin.purge` / `check-in`），一併在此處理。
 */

import type { StatusPillProps } from '@/components/shell';

type Tone = StatusPillProps['tone'];

/**
 * 資源段（路徑第二段 / 點分動作的實體部分）→ 中文名詞。
 * key 對應 `apps/api/src/routes/` 下的資源檔名。
 */
export const AUDIT_ENTITY_LABELS: Record<string, string> = {
  cases: '案件',
  clients: '客戶',
  client: '客戶', // service 顯式動作 client.update 使用單數形
  certificates: '證書',
  decisions: '驗證決定',
  'audit-tasks': '稽核任務',
  'audit-execution': '稽核執行',
  'audit-resources': '稽核資源',
  'nc-reports': '不符合報告',
  notifications: '通知',
  settings: '系統設定',
  'contract-reviews': '合約評審',
  'case-document-reviews': '申請文件審核',
  'form-instances': '表單',
  'form-templates': '表單',
  'report-styles': '報告樣式',
  files: '檔案',
  'recycle-bin': '回收站',
  bulletin: '公告',
  sync: '離線同步',
  me: '個人設定',
  auth: '帳號認證',
  'certified-org-directory': '認證機構名錄',
  sla: 'SLA 期限',
  analytics: '統計分析',
  dashboard: '工作台',
  jobs: '任務佇列',
  ai: 'AI 服務',
  tc: '待辦事項',
  'audit-log': '操作日誌',
  public: '公開查詢',
};

/**
 * 子資源段 → 中文名詞（接在實體名詞後組成複合對象，例：案件 + 付款 = 案件付款）。
 */
const SUB_NOUN: Record<string, string> = {
  payments: '付款',
  quotations: '報價單',
  samples: '樣品',
  contacts: '聯絡人',
  lands: '場域',
  products: '產品',
  members: '成員',
  documents: '文件',
  versions: '版本',
  blocks: '區塊',
  deliveries: '寄送',
  'audit-report': '稽核報告',
  'client-settings': '客戶端設定',
  reviews: '審核',
};

/**
 * 動作後綴段 / 點分動作的動詞部分 → 中文動詞。
 * 命中時取代「HTTP 方法動詞」，並從對象名詞鏈中移除該段。
 */
const ACTION_VERB: Record<string, string> = {
  confirm: '確認',
  'confirm-dispatch': '確認派案',
  dispatch: '派案',
  'read-all': '全部標為已讀',
  read: '標為已讀',
  approve: '核准',
  reject: '駁回',
  finalize: '定稿',
  draft: '生成草稿',
  regenerate: '重新生成',
  'schedule-meeting': '排程會議',
  schedule: '排程',
  votes: '投票',
  submit: '提交',
  restore: '還原',
  purge: '徹底刪除',
  'check-in': '簽到',
  update: '更新',
};

/** HTTP 方法 → 預設動詞 */
const METHOD_VERB: Record<string, string> = {
  POST: '新增',
  PUT: '更新',
  PATCH: '更新',
  DELETE: '刪除',
  GET: '查看',
};

/** 篩選下拉用的中文「動作分類」選項（value 為 HTTP 方法，供後端 method 前綴過濾） */
export const AUDIT_METHOD_OPTIONS: { value: string; label: string }[] = [
  { value: 'POST', label: '新增' },
  { value: 'PATCH', label: '更新' },
  { value: 'DELETE', label: '刪除' },
  { value: 'GET', label: '查看' },
];

/**
 * 依動詞判定 StatusPill 色調。
 * 刪除/駁回/徹底刪除 → danger；新增/確認/核准/還原/定稿 → success；
 * 查看/標為已讀 → muted；其餘（更新類）→ brand。
 */
function verbTone(verb: string): Tone {
  if (/刪除|駁回|徹底/.test(verb)) return 'danger';
  if (/新增|確認|核准|還原|定稿|派案/.test(verb)) return 'success';
  if (/查看|已讀/.test(verb)) return 'muted';
  return 'brand';
}

/**
 * 把實體類型字串翻成中文（找不到映射時原樣返回）。
 *
 * @param entityType 後端 entityType 欄位（路徑第二段）
 * @returns          中文名詞
 */
export function auditEntityLabel(entityType: string | null | undefined): string {
  if (!entityType) return '—';
  return AUDIT_ENTITY_LABELS[entityType] ?? entityType;
}

/** 翻譯結果 */
export interface AuditActionDisplay {
  /** 中文「對象 · 動作」短語 */
  text: string;
  /** StatusPill 色調 */
  tone: Tone;
}

/**
 * 把 audit log 的 `action` 欄位翻成中文「對象 · 動作」短語。
 *
 * 支援兩種來源：
 * 1. 中間件自動格式：`METHOD /api/<entity>/:id/<sub>/.../<action?>`
 * 2. service 顯式點分格式：`<entity>.<verb>` 或裸動作（如 `check-in`）
 *
 * 任何無法解析的輸入都回退為原始字串，確保不會白屏。
 *
 * @param action 後端 action 欄位
 * @returns      `{ text, tone }`
 */
export function formatAuditAction(action: string | null | undefined): AuditActionDisplay {
  if (!action) return { text: '—', tone: 'neutral' };

  // ── 情況 2：顯式點分動作（無空格）──────────────────────────
  if (!action.includes(' ')) {
    if (action.includes('.')) {
      const [ent = '', verb = ''] = action.split('.');
      const noun = AUDIT_ENTITY_LABELS[ent] ?? ent;
      const v = ACTION_VERB[verb] ?? verb;
      return { text: `${noun} · ${v}`, tone: verbTone(v) };
    }
    // 裸動作（例：check-in → 簽到）
    const v = ACTION_VERB[action] ?? action;
    return { text: v, tone: verbTone(v) };
  }

  // ── 情況 1：HTTP 方法 + 路徑 ───────────────────────────────
  const [method = '', rawPath = ''] = action.split(' ');
  // 去掉 /api/ 前綴與 :id 佔位段，僅留有意義的資源/動作段
  const segs = rawPath
    .replace(/^\/api\//, '')
    .split('/')
    // 濾掉 :id 佔位段與裸數字 ID（後端通常已標準化為 :id，此處再保險一層）
    .filter((s) => s && s !== ':id' && !/^\d+$/.test(s));

  const entity = segs[0];
  if (!entity) {
    return { text: action, tone: 'neutral' };
  }

  const rest = segs.slice(1);

  let verb = METHOD_VERB[method] ?? method;
  const nounSegs: string[] = [];

  // settings 模組的子項本身即完整名詞，不疊加「系統設定」前綴避免「系統設定客戶端設定」
  if (!(entity === 'settings' && rest.length > 0)) {
    nounSegs.push(entity);
  }

  rest.forEach((seg, idx) => {
    const isLast = idx === rest.length - 1;
    // 末段若是已知動作後綴，取代動詞且不計入名詞
    if (isLast && ACTION_VERB[seg]) {
      verb = ACTION_VERB[seg];
      return;
    }
    nounSegs.push(seg);
  });

  const noun = nounSegs.map((s) => AUDIT_ENTITY_LABELS[s] ?? SUB_NOUN[s] ?? s).join('');

  const text = noun ? `${noun} · ${verb}` : verb;
  return { text, tone: verbTone(verb) };
}
